// types
import { IPlanItem } from 'types/graphql'
// constants
import { ENTITIES } from 'constants/index'

export type TSourceItem = { entityId?: string; treePosition?: number }

interface IResolveArgs {
  // the node being added (a source-plan item) — carries its source treePosition + entity id
  node: { entityId?: string; treePosition?: number }
  // items already present in the destination plan (from PARENT_PLAN_SHORT_DETAILS_TREE_QUERY)
  destinationItems: IPlanItem[]
  // all items of the source plan, used to know the reference order
  sourceItems: TSourceItem[]
}

interface IResolveResult {
  insertPosition: number
  // destination items that must be pushed down by +1 to free the insert slot
  shiftedItems: { id: string; treePosition: number }[]
}

const getEntityId = (item: any): string | null =>
  item?.planObjective?.id || item?.planAction?.id || item?.planKPI?.id

/**
 * Resolves where a referenced item should land in the destination plan so that
 * referenced items keep the same relative order as in the source plan.
 *
 * Example: source order is 1,2,3,4,5 and 2,4 are already added. Adding 3 places
 * it right after 2 (and before 4), regardless of any original destination items
 * that sit between them. Since the backend stores `treePosition` as-is, every
 * destination item at or after the target slot is shifted down by one.
 */
export const resolveReferencedInsertPosition = ({
  node,
  destinationItems,
  sourceItems
}: IResolveArgs): IResolveResult => {
  // keep only items with a real treePosition so every position below is a plain number
  const items = (destinationItems || [])
  // const items = (destinationItems || []).filter((item) => typeof item?.treePosition === 'number')
  const appendPosition = items.length ? Math.max(...items.map((item) => item.treePosition)) + 1 : 0

  const newSourcePosition = node?.treePosition

  // source treePosition keyed by entity id — lets us rank existing referenced items
  const sourcePositionByEntityId = new Map<string, number>()

  for (const item of sourceItems || []) {
    sourcePositionByEntityId.set(item.entityId, item.treePosition)
  }

  const lowerPositions: number[] = []
  const higherPositions: number[] = []

  items.forEach((item) => {
    const entityId = getEntityId(item)
    // originals (not referenced from the source plan) have no source rank — skip them
    if (!entityId || !sourcePositionByEntityId.has(entityId)) {
      return
    }

    const sourcePosition = sourcePositionByEntityId.get(entityId)

    if (sourcePosition < newSourcePosition) {
      lowerPositions.push(item.treePosition)
    } else if (sourcePosition > newSourcePosition) {
      higherPositions.push(item.treePosition)
    }
  })

  let insertPosition: number

  if (lowerPositions.length) {
    // right after the last already-added item that precedes it in the source order
    insertPosition = Math.max(...lowerPositions) + 1
  } else if (higherPositions.length) {
    // no predecessor — go right before the first already-added item that follows it
    insertPosition = Math.min(...higherPositions)
  } else {
    // nothing comparable yet — append to the end
    insertPosition = appendPosition
  }

  const shiftedItems = items
    .filter((item) => item.treePosition >= insertPosition)
    .map((item) => ({ id: item.id, treePosition: item.treePosition + 1 }))

  return { insertPosition, shiftedItems }
}

/** Maps a plan-item entity type to the corresponding create-input id field. */
export const getEntityIdKey = (entityType: keyof typeof ENTITIES) => {
  switch (entityType) {
    case ENTITIES.PLAN_OBJECTIVE:
      return 'planObjectiveId'

    case ENTITIES.PLAN_ACTION:
      return 'planActionId'

    case ENTITIES.PLAN_KPI:
      return 'planKPIId'

    default:
      return null
  }
}

interface IReserveArgs extends IResolveArgs {
  updatePlanTreeMutation?: (options: { variables: { input: IResolveResult['shiftedItems'] } }) => Promise<any>
}

/**
 * Frees the target slot and returns the resolved insert position: pushes every
 * destination item at or after the slot down by one, then hands back where the
 * new referenced item should be created. Throws if the shift mutation fails.
 */
export const reserveReferencedInsertPosition = async ({
  node,
  destinationItems,
  sourceItems,
  updatePlanTreeMutation
}: IReserveArgs): Promise<number> => {
  const { insertPosition, shiftedItems } = resolveReferencedInsertPosition({ node, destinationItems, sourceItems })

  if (shiftedItems.length) {
    await updatePlanTreeMutation({ variables: { input: shiftedItems } })
  }

  return insertPosition
}
